import { postUserLogin } from "../../api/postUserLogin";
import { refreshAccessToken } from "../../api/refreshAccessToken"; // credentials: "include"
import { verifyAccessToken } from "../../api/verifyAccessToken"; // credentials: "include"
import { postUserLogout } from "../../api/postUserLogout"; // credentials: "include"
import { getUserSignatureProfile } from "../../utils/userSignatureProfile";
import { disableWebPushNotifications } from "../../notifications/webPushSubscriptions";

// ✅ auth 디버그 로그 ON/OFF
const AUTH_DEBUG = false; // 필요하면 false로

const _ts = () => new Date().toISOString().slice(11, 23); // HH:MM:SS.mmm
const _mkTrace = (p = "AUTH") =>
  `${p}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

const _pickRes = (r) =>
  r ? { success: r.success, code: r.code, message: r.message } : r;

const _pickErr = (e) => ({
  name: e?.name,
  message: e?.message,
  code: e?.code,
  // 너무 길면 보기 힘드니 상단 2~3줄만
  stack: (e?.stack || "").split("\n").slice(0, 3).join("\n"),
});

let refreshPromise = null; // (기존 유지) :contentReference[oaicite:2]{index=2}
let refreshTrace = null;

// ✅ bootstrap 중에는 같은 trace로 me/refresh/setter 로그가 이어지게
let activeTrace = null;

function _log(trace, label, data) {
  if (!AUTH_DEBUG) return;
  if (data !== undefined) console.log(`[${_ts()}] ${trace} ${label}`, data);
  else console.log(`[${_ts()}] ${trace} ${label}`);
}

function _group(trace, title, data) {
  if (!AUTH_DEBUG) return;
  console.groupCollapsed(`[${_ts()}] ${trace} ${title}`);
  if (data !== undefined) console.log(data);
}

function _endGroup() {
  if (!AUTH_DEBUG) return;
  console.groupEnd();
}

async function _timed(trace, label, fn) {
  const t0 = performance.now();
  _log(trace, `▶ ${label}`);
  try {
    const out = await fn();
    _log(trace, `✅ ${label} (+${(performance.now() - t0).toFixed(1)}ms)`);
    return out;
  } catch (e) {
    _log(
      trace,
      `❌ ${label} (+${(performance.now() - t0).toFixed(1)}ms)`,
      _pickErr(e)
    );
    throw e;
  }
}

export const createAuthSlice = (set, get) => ({
  // ───────────────────────────────────────────────
  // 🔐 인증 상태 (추천: status로 통일)
  // ───────────────────────────────────────────────
  status: "unknown", // "unknown" | "authenticated"(인증됨) | "unauthenticated(인증되지 않은)"
  user: null,
  authLoading: false, // login/logout 같은 명시적 액션용
  bootstrapping: false, // 새로고침 복구용
  authError: null,

  // ───────────────────────────────────────────────
  // 내부 setter
  // ───────────────────────────────────────────────
  setAuthenticated: (user) => {
    const trace = activeTrace || _mkTrace("SET");
    const normalizedUser = normalizeUser(user, get().user);
    _log(trace, "setAuthenticated()", {
      id: normalizedUser?.id,
      user_name: normalizedUser?.user_name,
    });

    return set(
      {
        status: "authenticated",
        user: normalizedUser,
        authError: null,
        authLoading: false,
        bootstrapping: false,
      },
      false,
      "auth/setAuthenticated"
    );
  },

  setUnauthenticated: () => {
    const trace = activeTrace || _mkTrace("SET");
    _log(trace, "setUnauthenticated()");

    return set(
      {
        status: "unauthenticated",
        user: null,
        authError: null,
        authLoading: false,
        bootstrapping: false,
      },
      false,
      "auth/setUnauthenticated"
    );
  },

  setLastSelectedProject: (projectId, projectName) =>
    set((s) => ({
      user: {
        ...s.user,
        last_selected_project: projectId,
        last_selected_project_name: projectName,
      },
    })),

  // ───────────────────────────────────────────────
  // 서버 me(세션 확인) / refresh
  // ───────────────────────────────────────────────
  me: async () => {
    const trace = activeTrace || _mkTrace("ME");

    const res = await _timed(trace, "verifyAccessToken()", () =>
      verifyAccessToken()
    );
    _log(trace, "verifyAccessToken ->", _pickRes(res));

    if (!res?.success) {
      const err = new Error(res?.message || "UNAUTHORIZED");
      err.code = res?.code || 401; // (기존 유지) :contentReference[oaicite:3]{index=3}
      _log(trace, "me() throw", _pickErr(err));
      throw err;
    }

    _log(trace, "me() OK user", {
      id: res?.user?.id,
      user_name: res?.user?.user_name,
    });
    return res.user;
  },

  refresh: async () => {
    const trace = activeTrace || _mkTrace("REFRESH");

    // ✅ refreshPromise 재사용 로그 (동시 호출 시 특히 중요)
    if (refreshPromise) {
      _log(trace, `reuse refreshPromise (owner=${refreshTrace})`);
      return refreshPromise;
    }

    refreshTrace = trace;

    refreshPromise = (async () => {
      const r = await _timed(trace, "refreshAccessToken()", () =>
        refreshAccessToken()
      );
      _log(trace, "refreshAccessToken ->", _pickRes(r));

      if (!r?.success) {
        const err = new Error(r?.message || "REFRESH_FAILED");
        err.code = r?.code || 401;
        _log(trace, "refresh() throw", _pickErr(err));
        throw err;
      }

      return true;
    })().finally(() => {
      _log(trace, "refreshPromise cleared");
      refreshPromise = null;
      refreshTrace = null;
    });

    return refreshPromise;
  },

  // ───────────────────────────────────────────────
  // ✅ 새로고침/앱 최초 진입 시 1회만 호출
  // ───────────────────────────────────────────────
  bootstrap: async () => {
    const trace = _mkTrace("BOOT");

    if (get().bootstrapping) {
      _log(trace, "skip: already bootstrapping", {
        status: get().status,
        bootstrapping: get().bootstrapping,
      });
      return;
    }

    activeTrace = trace;
    _group(trace, "bootstrap START", {
      status: get().status,
      bootstrapping: get().bootstrapping,
    });

    set(
      { bootstrapping: true, authError: null },
      false,
      "auth/bootstrap:start"
    );
    _log(trace, "set bootstrapping=true");

    try {
      // 1) me 먼저
      _log(trace, "STEP1: call me()");
      const user = await get().me();
      _log(trace, "STEP1: me() returned", {
        id: user?.id,
        user_name: user?.user_name,
      });

      get().setAuthenticated(normalizeUser(user));
      _log(trace, "DONE: authenticated");
      return true;
    } catch (e) {
      _log(trace, "STEP1: me() failed", _pickErr(e));

      // TOKEN_EXPIRED(만료) 또는 TOKEN_MISSING(쿠키 없음)이면 refresh 시도
      const shouldRefresh =
        e?.code === "TOKEN_EXPIRED" || e?.code === "TOKEN_MISSING";

      if (!shouldRefresh) {
        _log(trace, "BRANCH: not EXPIRED/MISSING -> setUnauthenticated()", {
          code: e?.code,
        });
        get().setUnauthenticated();
        return false;
      }

      // 2) 만료면 refresh → me 재시도
      try {
        _log(trace, "STEP2: call refresh()");
        await get().refresh();

        _log(trace, "STEP3: call me() again");
        const user2 = await get().me();
        _log(trace, "STEP3: me2() returned", {
          id: user2?.id,
          user_name: user2?.user_name,
        });

        get().setAuthenticated(normalizeUser(user2));
        _log(trace, "DONE: authenticated(after refresh)");
        return true;
      } catch (e2) {
        _log(trace, "STEP2/3 failed -> setUnauthenticated()", _pickErr(e2));
        get().setUnauthenticated();
        return false;
      }
    } finally {
      _log(trace, "bootstrap END", {
        status: get().status,
        bootstrapping: get().bootstrapping,
      });
      _endGroup();
      activeTrace = null;
    }
  },

  // ───────────────────────────────────────────────
  // 로그인
  // ───────────────────────────────────────────────
  login: async (email, password, options = {}) => {
    set({ authLoading: true, authError: null }, false, "auth/login:start");

    const res = await postUserLogin(email, password, options);
    if (!res || !res.success) {
      const failMessage = res?.message || "Login failed";
      set(
        { authLoading: false, authError: failMessage },
        false,
        "auth/login:fail"
      );
      return {
        success: false,
        message: failMessage,
        messageKey: res?.messageKey || "",
        messageParams: res?.messageParams,
        code: res?.code || "",
        data: res?.data,
      };
    }

    let user = normalizeUser(res.user);
    if (!user) {
      try {
        const meUser = await get().me();
        user = normalizeUser(meUser);
      } catch (e) {
        get().setUnauthenticated();
        return {
          success: false,
          message: "Session check failed",
          messageKey: "api.common.serverError",
        };
      }
    }

    set(
      { status: "authenticated", user, authLoading: false, authError: null },
      false,
      "auth/login:success"
    );

    return {
      success: true,
      message: res.message,
      messageKey: res.messageKey || "",
      messageParams: res.messageParams,
      last_selected_project: user?.last_selected_project ?? null,
    };
  },

  logout: async () => {
    set({ authLoading: true, authError: null }, false, "auth/logout:start");

    try {
      await disableWebPushNotifications();
    } catch (e) {
      console.warn("⚠️ 브라우저 알림 구독 해제 실패:", e);
    }

    try {
      await postUserLogout(); // 서버 쿠키 삭제
    } catch (e) {
      console.warn("⚠️ 서버 로그아웃 실패:", e);
      // 실패해도 프론트 상태는 로그아웃 처리하는 게 UX상 맞음
    }

    // 다른 slice 초기화
    if (get().clearProjectTree) get().clearProjectTree();

    get().setUnauthenticated();
    return true;
  },
});

// ✅ user 형태 정규화(네 기존 형태 유지)
function normalizePermission(permission) {
  if (!permission) return {};
  if (typeof permission === "object") return permission;

  try {
    const parsed = JSON.parse(permission);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function normalizeUser(u, previousUser = null) {
  if (!u && !previousUser) return null;

  const source = {
    ...(previousUser || {}),
    ...(u || {}),
  };

  const [local, domain] = source.email ? source.email.split("@") : [null, null];
  const hasExplicitSignatureProfile =
    u &&
    (Object.prototype.hasOwnProperty.call(u, "signature_image_url") ||
      Object.prototype.hasOwnProperty.call(u, "signatureImageUrl") ||
      Object.prototype.hasOwnProperty.call(u, "signatureProfile"));
  const signatureProfile = hasExplicitSignatureProfile
    ? getUserSignatureProfile(u)
    : getUserSignatureProfile(source) || previousUser?.signatureProfile || null;

  return {
    id: source.id ?? null,
    user_name: source.user_name ?? null,
    lastLogin: source.lastLogin ?? source.last_login ?? null,
    role: source.role,
    organization_id: source.organization_id ?? null,
    organization_root_id: source.organization_root_id ?? null,
    organization_role: source.organization_role ?? null,
    organization_name: source.organization_name ?? null,
    organization_country_code: source.organization_country_code ?? null,
    organization_is_active: source.organization_is_active ?? null,
    permission: normalizePermission(source.permission),
    country_id: source.country_id ?? null,
    country_code: source.country_code ?? null,
    country_name: source.country_name ?? null,
    last_selected_project: source.last_selected_project ?? null,
    last_selected_project_name: source.last_selected_project_name ?? null,
    email: source.email ?? null,
    local: local,
    domain: domain,
    signatureProfile,
  };
}
